import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Shield, Trash2, UserX, Loader2, AlertTriangle } from 'lucide-react';
import { X, User, Key, ShieldCheck, UserCheck, Eye, EyeOff } from './icons';
import api from '../services/api';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const TABS = [
    { id: 'general', label: 'General', icon: User },
    { id: 'apps', label: 'Rol API', icon: ShieldCheck },
];

export default function UserManagementModal({ isOpen, onClose, user, onUserUpdated, showToast }) {
    const { user: currentUser } = useAuth();
    const esAdmin = currentUser?.role === 'admin';
    const [tab, setTab] = useState('general');

    // --- General ---
    const [formData, setFormData] = useState({ username: '', email: '', nombre: '', apellido: '', role: 'soporte', isActive: true });
    const [savingGeneral, setSavingGeneral] = useState(false);
    const [errorGeneral, setErrorGeneral] = useState('');

    // --- Identidad central (api_vicsa): rol de API, AuthMethod, contraseña ---
    const [rolCentral, setRolCentral] = useState('usuario');
    const [authMethod, setAuthMethod] = useState('sso');
    const [savingRol, setSavingRol] = useState(false);
    const [savingAuthMethod, setSavingAuthMethod] = useState(false);
    const [errorAuthMethod, setErrorAuthMethod] = useState('');
    const [generandoPassword, setGenerandoPassword] = useState(false);
    const [passwordGenerada, setPasswordGenerada] = useState('');
    const [errorGenerarPassword, setErrorGenerarPassword] = useState('');

    // --- Contraseña central manual ---
    const [passwordManual, setPasswordManual] = useState('');
    const [confirmPasswordManual, setConfirmPasswordManual] = useState('');
    const [showPasswordManual, setShowPasswordManual] = useState(false);
    const [guardandoPasswordManual, setGuardandoPasswordManual] = useState(false);
    const [errorPasswordManual, setErrorPasswordManual] = useState('');

    // --- Eliminar ---
    const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);

    const tieneCuentaLocal = user?.localId !== null && user?.localId !== undefined;

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username || '',
                email: user.email || '',
                nombre: user.nombre || '',
                apellido: user.apellido || '',
                role: user.rolHelpdesk || 'soporte',
                isActive: user.activoHelpdesk !== undefined && user.activoHelpdesk !== null ? user.activoHelpdesk : true,
            });
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            setRolCentral(user.rolCentral || 'usuario');
            setAuthMethod(user.authMethod || 'sso');
            setPasswordGenerada('');
            setErrorAuthMethod('');
            setErrorGenerarPassword('');
            setPasswordManual('');
            setConfirmPasswordManual('');
            setErrorPasswordManual('');
        }
    }, [user]);

    useEffect(() => {
        if (isOpen) {
            setTab('general');
            setErrorGeneral('');
            setConfirmandoEliminar(false);
        }
    }, [isOpen, user?.id]);

    if (!isOpen || !user) return null;

    const nombreCompleto = [user.nombre, user.apellido].filter(Boolean).join(' ') || user.username || user.email;

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const guardarGeneral = async (e) => {
        e.preventDefault();
        setSavingGeneral(true);
        setErrorGeneral('');
        try {
            const payload = {};
            if (formData.username !== user.username) payload.username = formData.username;
            if ((formData.email || '') !== (user.email || '')) payload.email = formData.email;
            if (esAdmin && formData.role !== user.rolHelpdesk) payload.role = (formData.role || '').toLowerCase();
            if ((formData.nombre || '') !== (user.nombre || '')) payload.nombre = formData.nombre;
            if ((formData.apellido || '') !== (user.apellido || '')) payload.apellido = formData.apellido;
            if (formData.isActive !== user.activoHelpdesk) payload.isActive = formData.isActive;

            if (Object.keys(payload).length > 0) {
                await api.put(`/auth/${user.localId}`, payload);
                onUserUpdated();
                showToast?.('Éxito', 'Usuario actualizado correctamente', 'success');
            }
            onClose();
        } catch (err) {
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            setErrorGeneral(serverMsg || 'Error al actualizar usuario');
        } finally {
            setSavingGeneral(false);
        }
    };

    const guardarPasswordManual = async (e) => {
        e.preventDefault();
        setErrorPasswordManual('');

        if (!passwordManual) {
            setErrorPasswordManual('La contraseña es obligatoria');
            return;
        }
        if (passwordManual.length < 10 || !/[A-Z]/.test(passwordManual) || !/[0-9]/.test(passwordManual)) {
            setErrorPasswordManual('La contraseña debe tener al menos 10 caracteres, una mayúscula y un número');
            return;
        }
        if (passwordManual !== confirmPasswordManual) {
            setErrorPasswordManual('Las contraseñas no coinciden');
            return;
        }

        setGuardandoPasswordManual(true);
        try {
            const { data } = await authAPI.actualizarUsuarioCentral(user.centralId, { password: passwordManual });
            if (data.AuthMethod) setAuthMethod(data.AuthMethod);
            showToast?.('Éxito', 'Contraseña actualizada correctamente', 'success');
            setPasswordManual('');
            setConfirmPasswordManual('');
        } catch (err) {
            setErrorPasswordManual(err.response?.data?.message || err.response?.data?.error || 'Error al actualizar la contraseña');
        } finally {
            setGuardandoPasswordManual(false);
        }
    };

    const guardarRolCentral = async (nuevoRol) => {
        setSavingRol(true);
        try {
            await authAPI.actualizarUsuarioCentral(user.centralId, { rol: nuevoRol });
            setRolCentral(nuevoRol);
            showToast?.('Éxito', 'Rol de API actualizado correctamente', 'success');
        } catch (err) {
            showToast?.('Error', err.response?.data?.message || 'Error al actualizar el rol', 'error');
        } finally {
            setSavingRol(false);
        }
    };

    const guardarAuthMethod = async (nuevoMetodo) => {
        setSavingAuthMethod(true);
        setErrorAuthMethod('');
        try {
            await authAPI.actualizarUsuarioCentral(user.centralId, { authMethod: nuevoMetodo });
            setAuthMethod(nuevoMetodo);
            showToast?.('Éxito', 'Método de autenticación actualizado', 'success');
        } catch (err) {
            setErrorAuthMethod(err.response?.data?.message || err.response?.data?.error || 'Error al actualizar el método de autenticación');
        } finally {
            setSavingAuthMethod(false);
        }
    };

    const generarPasswordCentral = async () => {
        setGenerandoPassword(true);
        setErrorGenerarPassword('');
        setPasswordGenerada('');
        try {
            const { data } = await authAPI.actualizarUsuarioCentral(user.centralId, { generarPassword: true });
            setPasswordGenerada(data.passwordGenerada);
            if (data.AuthMethod) setAuthMethod(data.AuthMethod);
            showToast?.('Éxito', 'Contraseña generada', 'success');
        } catch (err) {
            setErrorGenerarPassword(err.response?.data?.message || err.response?.data?.error || 'Error al generar la contraseña');
        } finally {
            setGenerandoPassword(false);
        }
    };

    const toggleEstado = async () => {
        const action = user.activoHelpdesk ? 'desactivar' : 'activar';
        try {
            await api.put(`/auth/${user.localId}`, { isActive: !user.activoHelpdesk });
            showToast?.('Éxito', `Usuario ${action}do correctamente`, 'success');
            onUserUpdated();
            onClose();
        } catch (err) {
            showToast?.('Error', 'Error al cambiar el estado del usuario', 'error');
        }
    };

    const eliminarUsuario = async () => {
        try {
            await api.delete(`/auth/${user.localId}`);
            showToast?.('Éxito', 'Usuario eliminado correctamente', 'success');
            onUserUpdated();
            onClose();
        } catch (err) {
            showToast?.('Error', 'Error al eliminar el usuario', 'error');
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-lg max-h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center flex-shrink-0">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{nombreCompleto}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{user.email || 'Sin correo'}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.id
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                                }`}
                        >
                            <t.icon className="w-4 h-4" /> {t.label}
                        </button>
                    ))}
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {tab === 'general' && (
                        <form onSubmit={guardarGeneral} className="space-y-4">
                            {!tieneCuentaLocal && (
                                <div className="text-sm text-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-300 p-3 rounded-lg flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                    <span>Este usuario no tiene cuenta en HelpDesk todavía — inicia sesión una vez (local o Microsoft) para poder gestionar esto.</span>
                                </div>
                            )}
                            {errorGeneral && (
                                <div className="bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-2 rounded">
                                    {errorGeneral}
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    <User className="w-4 h-4 inline mr-1" /> Nombre de usuario
                                </label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    disabled={!esAdmin || !tieneCuentaLocal}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            {Boolean(user.username) && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Correo electrónico</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        disabled={!esAdmin || !tieneCuentaLocal}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleInputChange}
                                        disabled={!esAdmin || !tieneCuentaLocal}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido</label>
                                    <input
                                        type="text"
                                        name="apellido"
                                        value={formData.apellido}
                                        onChange={handleInputChange}
                                        disabled={!esAdmin || !tieneCuentaLocal}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                            {esAdmin && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        <Shield className="w-4 h-4 inline mr-1" /> Rol
                                    </label>
                                    <select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="admin">Administrador</option>
                                        <option value="soporte">Staff</option>
                                        <option value="usuario">Usuario</option>
                                    </select>
                                </div>
                            )}

                            <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={toggleEstado}
                                    disabled={!esAdmin || !tieneCuentaLocal}
                                    className={`flex items-center gap-1.5 text-sm font-medium ${user.activoHelpdesk ? 'text-gray-600 dark:text-gray-300 hover:text-gray-800' : 'text-green-600 hover:text-green-800'} disabled:opacity-50`}
                                >
                                    {user.activoHelpdesk ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                    {user.activoHelpdesk ? 'Desactivar cuenta' : 'Activar cuenta'}
                                </button>

                                <button
                                    type="submit"
                                    disabled={savingGeneral || !esAdmin || !tieneCuentaLocal}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                                >
                                    {savingGeneral ? 'Guardando...' : 'Guardar cambios'}
                                </button>
                            </div>

                            <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                                {!confirmandoEliminar ? (
                                    <button
                                        type="button"
                                        onClick={() => setConfirmandoEliminar(true)}
                                        disabled={!esAdmin || !tieneCuentaLocal}
                                        className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                                    >
                                        <Trash2 className="w-4 h-4" /> Eliminar cuenta
                                    </button>
                                ) : (
                                    <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg flex items-start gap-2">
                                        <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                                                Esta acción no se puede deshacer. ¿Eliminar a {nombreCompleto}?
                                            </p>
                                            <div className="flex gap-2">
                                                <button type="button" onClick={eliminarUsuario} className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                                                    Sí, eliminar
                                                </button>
                                                <button type="button" onClick={() => setConfirmandoEliminar(false)} className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm">
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    )}


                    {tab === 'apps' && (
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Rol en la identidad central (api_vicsa) — controla qué puede hacer este usuario en la API de integraciones.
                                </p>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        <Shield className="w-4 h-4 inline mr-1" /> Rol de la API
                                    </label>
                                    <select
                                        value={rolCentral}
                                        disabled={savingRol || !esAdmin}
                                        onChange={(e) => guardarRolCentral(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="usuario">usuario — Acceso Limitado (solo lectura)</option>
                                        <option value="integracion">integracion — API Automática (sistema a sistema)</option>
                                        <option value="admin">admin — Acceso Completo</option>
                                    </select>
                                    {savingRol && (
                                        <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                                            <Loader2 className="w-3 h-3 animate-spin" /> Guardando...
                                        </p>
                                    )}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                                    <strong>usuario:</strong> Puede consultar la API con su token Microsoft.<br />
                                    <strong>integracion:</strong> Cuenta de sistema, sin restricciones de rate-limit de usuario final.<br />
                                    <strong>admin:</strong> Acceso total, puede gestionar otros usuarios vía API.
                                </div>
                            </div>

                            <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Método de autenticación central — válido para todas las apps (HelpDesk, Autogestión, Monitor).
                                </p>
                                {errorAuthMethod && (
                                    <div className="bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-2 rounded text-sm">
                                        {errorAuthMethod}
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        <ShieldCheck className="w-4 h-4 inline mr-1" /> Método
                                    </label>
                                    <select
                                        value={authMethod}
                                        disabled={savingAuthMethod || !esAdmin}
                                        onChange={(e) => guardarAuthMethod(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="sso">sso — Solo Microsoft</option>
                                        <option value="local">local — Solo correo y contraseña</option>
                                        <option value="ambos">ambos — Microsoft o contraseña</option>
                                    </select>
                                    {savingAuthMethod && (
                                        <p className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                                            <Loader2 className="w-3 h-3 animate-spin" /> Guardando...
                                        </p>
                                    )}
                                </div>

                                {errorGenerarPassword && (
                                    <div className="bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-2 rounded text-sm">
                                        {errorGenerarPassword}
                                    </div>
                                )}

                                {passwordGenerada ? (
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg p-3 space-y-2">
                                        <p className="text-sm text-green-700 dark:text-green-300">
                                            Esta contraseña no se puede volver a mostrar. Guárdala o compártela con el usuario ahora.
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono select-all">
                                                {passwordGenerada}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={() => navigator.clipboard.writeText(passwordGenerada)}
                                                className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                                            >
                                                Copiar
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={generarPasswordCentral}
                                        disabled={generandoPassword || !esAdmin}
                                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                                    >
                                        {generandoPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                                        Generar contraseña
                                    </button>
                                )}
                            </div>

                            <form onSubmit={guardarPasswordManual} className="space-y-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    O escribir la contraseña central manualmente.
                                </p>
                                {errorPasswordManual && (
                                    <div className="bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-2 rounded text-sm">
                                        {errorPasswordManual}
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nueva contraseña</label>
                                    <div className="relative">
                                        <input
                                            type={showPasswordManual ? 'text' : 'password'}
                                            value={passwordManual}
                                            onChange={(e) => setPasswordManual(e.target.value)}
                                            disabled={!esAdmin}
                                            className="w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                        <button type="button" onClick={() => setShowPasswordManual((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                            {showPasswordManual ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirmar contraseña</label>
                                    <input
                                        type={showPasswordManual ? 'text' : 'password'}
                                        value={confirmPasswordManual}
                                        onChange={(e) => setConfirmPasswordManual(e.target.value)}
                                        disabled={!esAdmin}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div className="flex justify-end">
                                    <button type="submit" disabled={guardandoPasswordManual || !esAdmin} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
                                        {guardandoPasswordManual ? 'Guardando...' : 'Guardar contraseña'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
